import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import React, { FC } from 'react'

import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'

Font.register({
  family: 'myriad-pro',
  fonts: [
    {
      src: require('../../../../../assets/fonts/myriad-pro-regular.woff'),
      fontWeight: 400
    },
    {
      src: require('../../../../../assets/fonts/myriad-pro-bold.woff'),
      fontWeight: 700
    }
  ]
})
Font.registerHyphenationCallback(word => [word])

interface MediaPlanPdfProps {
  campaigns: MasterCampaignPlanCampaignBuilder[]
  ganttChartImage?: string
  campaignMapImages?: Record<string, string | null>
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: '1.25in',
    paddingLeft: '1.75in',
    paddingBottom: '1.25in',
    paddingRight: '1.75in',
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  root: {
    fontFamily: 'myriad-pro',
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  header: {
    fontFamily: 'myriad-pro',
    fontSize: '26pt',
    lineHeight: 1.2,
    marginBottom: '10pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  section: {
    marginBottom: '4pt'
  },
  sectionTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '17pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  channelContent: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  placeholderText: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  campaignCard: {
    marginBottom: '4pt'
  },
  campaignHeader: {
    marginBottom: '4pt'
  },
  campaignTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '15pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  campaignBasicInfo: {
    marginBottom: '4pt'
  },
  basicInfoRow: {
    flexDirection: 'row',
    marginBottom: '2pt'
  },
  basicInfoLabel: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    fontWeight: 700,
    width: '25%',
    wordWrap: 'break-word'
  },
  basicInfoValue: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    fontWeight: 400,
    width: '75%',
    wordWrap: 'break-word'
  },
  summarySection: {
    marginBottom: '4pt'
  },
  summaryTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  summaryDivider: {
    width: '100%',
    height: 1,
    backgroundColor: '#cccccc',
    marginBottom: '4pt'
  },
  summaryText: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  seasonalitySection: {
    marginBottom: '4pt'
  },
  geographySection: {
    marginBottom: '4pt'
  },
  audienceSection: {
    marginBottom: '4pt'
  },
  columnTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word'
  },
  columnDivider: {
    width: '100%',
    height: 1,
    backgroundColor: '#cccccc',
    marginBottom: '4pt'
  },
  columnDescription: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    fontWeight: 400,
    wordWrap: 'break-word'
  },
  listContainer: {
    marginTop: '4pt'
  },
  cityColumnsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cityColumn: {
    flex: 1,
    marginRight: '2%'
  },
  cityColumnLast: {
    flex: 1
  },
  cityGroup: {
    marginBottom: '4pt'
  },
  cityName: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '2pt',
    fontWeight: 700,
    wordWrap: 'break-word'
  },
  postalCode: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginLeft: '10pt',
    fontWeight: 400,
    wordWrap: 'break-word'
  },
  segmentGroup: {
    marginBottom: '4pt'
  },
  segmentName: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '2pt',
    fontWeight: 700,
    wordWrap: 'break-word'
  },
  segmentDescription: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginLeft: 0,
    fontWeight: 400,
    wordWrap: 'break-word'
  },
  emptyState: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  sectionBox: {
    backgroundColor: '#E6EDF2',
    borderRadius: '8pt',
    padding: '12pt',
    marginTop: '20pt',
    marginBottom: '8pt'
  },
  boxTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  boxContent: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  ganttPage: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: '1.75in',
    paddingLeft: '0in',
    paddingBottom: '1.75in',
    paddingRight: '0in',
    fontFamily: 'myriad-pro',
    justifyContent: 'center',
    alignItems: 'center'
  },
  ganttImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  ganttImageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    transform: 'rotate(-90deg)'
  },
  mapSection: {
    marginBottom: '20pt'
  },
  mapTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '17pt',
    lineHeight: 1.2,
    marginBottom: '10pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  mapImage: {
    width: '100%',
    maxHeight: '350pt',
    objectFit: 'contain'
  }
})

const MediaPlanPdf: FC<MediaPlanPdfProps> = ({ campaigns, ganttChartImage, campaignMapImages }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const sortedCampaigns = [...campaigns].sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())

  const renderCampaignDetails = (campaign: MasterCampaignPlanCampaignBuilder, index: number) => (
    <View key={campaign.id} style={styles.campaignCard}>
      <View style={styles.campaignHeader}>
        <Text style={styles.campaignTitle}>{campaign.name}</Text>
      </View>

      <View style={styles.campaignBasicInfo}>
        <View style={styles.basicInfoRow}>
          <Text style={styles.basicInfoLabel}>Campaign Type:</Text>
          <Text style={styles.basicInfoValue}>{campaign.isOffAdCritter && campaign.offAdCritterName ? `${campaign.offAdCritterName}` : campaign.campaignType}</Text>
        </View>
        <View style={styles.basicInfoRow}>
          <Text style={styles.basicInfoLabel}>Starts:</Text>
          <Text style={styles.basicInfoValue}>{moment(campaign.startDate).format('MMMM Do YYYY')}</Text>
        </View>
        <View style={styles.basicInfoRow}>
          <Text style={styles.basicInfoLabel}>Duration:</Text>
          <Text style={styles.basicInfoValue}>
            {campaign.intervalDuration || 0} {campaign.intervalUnit || 'months'}
          </Text>
        </View>
        <View style={styles.basicInfoRow}>
          <Text style={styles.basicInfoLabel}>Budget:</Text>
          <Text style={styles.basicInfoValue}>
            {formatCurrency(campaign.intervalBudget || 0)}/{campaign.intervalUnit || 'month'}
          </Text>
        </View>
      </View>

      {campaign.shortSummary && (
        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>Summary</Text>
          <View style={styles.summaryDivider} />
          <Text style={styles.summaryText}>{campaign.shortSummary}</Text>
        </View>
      )}

      {campaign.seasonalitySummary && (
        <View style={styles.seasonalitySection}>
          <Text style={styles.summaryTitle}>Timing</Text>
          <View style={styles.summaryDivider} />
          <Text style={styles.summaryText}>{campaign.seasonalitySummary}</Text>
        </View>
      )}

      {(campaign.geoSummary || (campaign.zips?.length ?? 0) > 0) && (
        <View style={styles.geographySection}>
          <Text style={styles.columnTitle}>Geography</Text>
          <View style={styles.columnDivider} />
          {campaign.geoSummary && <Text style={styles.columnDescription}>{campaign.geoSummary}</Text>}

          {(() => {
            const shouldShowMap = campaign.isOffAdCritter
              ? campaign.includeInProjections && campaign.includeInGeoHeatmap
              : true

            return shouldShowMap && campaignMapImages?.[campaign.id] ? (
              <View style={styles.mapSection}>
                <Image src={campaignMapImages[campaign.id]!} style={styles.mapImage} />
                {campaign.zips && campaign.zips.length > 0 && (
                  <View style={{ marginTop: '10pt' }}>
                    <Text style={styles.summaryText}>ZIP Codes: {campaign.zips.map(zip => zip.code || zip.name).join(', ')}</Text>
                  </View>
                )}
              </View>
            ) : (
              <View style={styles.listContainer}>
                {campaign.zips &&
                  campaign.zips.length > 0 &&
                  (() => {
                    const cityGroups: { [city: string]: string[] } = {}

                    campaign.zips.forEach(zip => {
                      if (zip.name && zip.name.includes(',')) {
                        const [, city] = zip.name.split(',')
                        const cityName = city.trim()
                        if (!cityGroups[cityName]) {
                          cityGroups[cityName] = []
                        }
                        cityGroups[cityName].push(zip.code || zip.name)
                      }
                    })

                    const cityEntries = Object.entries(cityGroups)
                    const leftColumn = cityEntries.filter((_, index) => index % 2 === 0)
                    const rightColumn = cityEntries.filter((_, index) => index % 2 === 1)

                    return (
                      <View style={styles.cityColumnsContainer}>
                        <View style={styles.cityColumn}>
                          {leftColumn.map(([city, codes], cityIndex) => (
                            <View key={`left-${cityIndex}`} style={styles.cityGroup}>
                              <Text style={styles.cityName}>{city}</Text>
                              {codes.map((code, codeIndex) => (
                                <Text key={codeIndex} style={styles.postalCode}>
                                  {code}
                                </Text>
                              ))}
                            </View>
                          ))}
                        </View>
                        <View style={styles.cityColumnLast}>
                          {rightColumn.map(([city, codes], cityIndex) => (
                            <View key={`right-${cityIndex}`} style={styles.cityGroup}>
                              <Text style={styles.cityName}>{city}</Text>
                              {codes.map((code, codeIndex) => (
                                <Text key={codeIndex} style={styles.postalCode}>
                                  {code}
                                </Text>
                              ))}
                            </View>
                          ))}
                        </View>
                      </View>
                    )
                  })()}
              </View>
            )
          })()}
        </View>
      )}

      {campaign.campaignType !== MasterCampaignPlanCampaignBuilderType.Billboard && (campaign.audienceSummary || (campaign.segmentTargets && campaign.segmentTargets.length > 0)) && (
        <View style={styles.sectionBox}>
          <Text style={styles.boxTitle}>Audience Segments</Text>
          {campaign.audienceSummary && <Text style={styles.boxContent}>{campaign.audienceSummary}</Text>}
          {campaign.segmentTargets && campaign.segmentTargets.length > 0 && (
            <View style={styles.listContainer}>
              {campaign.segmentTargets.map((segment, segIndex) => (
                <View key={segIndex} style={styles.segmentGroup}>
                  <Text style={styles.segmentName}>{segment.hierarchy[segment.hierarchy.length - 1] || segment.name}</Text>
                  <Text style={styles.segmentDescription}>Audience ID: {segment.id}</Text>
                  <Text style={styles.segmentDescription}>{segment.name}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  )

  const renderFirstCampaignWithTitle = (campaign: MasterCampaignPlanCampaignBuilder, index: number) => (
    <View key={`first-${index}`}>
      <View style={styles.section}>
        <Text style={styles.header}>Campaign Details</Text>
      </View>

      {renderCampaignDetails(campaign, index)}
    </View>
  )

  return (
    <Document>
      {ganttChartImage && (
        <Page size='LETTER' style={styles.ganttPage}>
          <View style={styles.ganttImageContainer}>
            <Image src={ganttChartImage} style={styles.ganttImage} />
          </View>
        </Page>
      )}
      {sortedCampaigns.length > 0 ? (
        <>
          <Page size='LETTER' style={styles.page}>
            <View style={styles.root}>{renderFirstCampaignWithTitle(sortedCampaigns[0], 0)}</View>
          </Page>
          {sortedCampaigns.slice(1).map((campaign, index) => {
            return (
              <Page size='LETTER' style={styles.page} key={campaign.id}>
                <View style={styles.root}>{renderCampaignDetails(campaign, index + 1)}</View>
              </Page>
            )
          })}
        </>
      ) : (
        <Page size='LETTER' style={styles.page}>
          <View style={styles.root}>
            <View style={styles.section}>
              <Text style={styles.header}>Campaign Details</Text>
            </View>

            <View style={styles.section}>
              <Text style={styles.emptyState}>No campaigns have been configured for this plan yet.</Text>
            </View>
          </View>
        </Page>
      )}
    </Document>
  )
}

export default MediaPlanPdf
